<template>
  <v-textarea ref="textarea" class="brand-textarea" v-bind="mergedAttrs">
    <!-- Custom character counter -->
    <template v-if="showCustomCounter" #counter>
      <span class="text-caption" :class="counterColorClass">{{ counterText }}</span>
    </template>

    <!-- Forward all other parent slots -->
    <template v-for="(_, slotName) in forwardedSlots" :key="slotName" #[slotName]="slotData">
      <slot :name="slotName" v-bind="slotData || {}" />
    </template>
  </v-textarea>
</template>

<script setup>
import { computed, useAttrs, useSlots, ref } from 'vue'
import { mergeAttrs } from '@/utils/vue-attrs'
import { useCharacterCounter } from '@/composables/useCharacterCounter'

// Define props
const props = defineProps({
  variant: {
    type: String,
    default: null,
  },
  maxlength: {
    type: Number,
    default: null,
  },
})

// Get attrs and slots
const attrs = useAttrs()
const slots = useSlots()

// Template ref
const textarea = ref(null)

// Character counter (always called unconditionally, safe when maxlength is null)
const currentLength = computed(() => (attrs.modelValue ?? attrs['model-value'] ?? '').toString().length)
const showCustomCounter = computed(() => props.maxlength != null && !slots.counter)
const { counterText, counterColorClass } = useCharacterCounter(currentLength, computed(() => props.maxlength))

// Filter out 'counter' slot from forwarding when custom counter is active
const forwardedSlots = computed(() => {
  if (!showCustomCounter.value) return slots
  const { counter, ...rest } = slots
  return rest
})

// Define variants
const VARIANTS = {
  basic: {
    variant: 'outlined',
    bg: 'neutral0',
    color: 'blue400',
    'base-color': 'neutral50',
    classes: ['brand-textarea--basic', 'rounded-lg'],
  },
}

// Computed common attributes
const commonAttrs = computed(() => {
  const variant = VARIANTS[props.variant]
  const classes = []
  const common = {
    'hide-details': 'auto',
    'validate-on': 'blur',
  }

  // Activate Vuetify's counter infrastructure when maxlength is set
  if (props.maxlength != null) {
    common.counter = props.maxlength
  }

  return {
    ...common,
    ...variant,
    class: [
      ...classes,
      ...(variant?.classes ?? []),
    ],
  }
})

// Merge attributes properly
const mergedAttrs = computed(() => {
  return mergeAttrs(commonAttrs.value, attrs)
})

// Methods
const focus = () => {
  textarea.value?.$el.querySelector('textarea')?.focus()
}

// Expose methods for parent component access
defineExpose({
  focus,
})

// Set inheritAttrs to false
defineOptions({
  inheritAttrs: false,
})
</script>

<style lang="scss" scoped>
.v-textarea.brand-textarea {
  &.brand-textarea--basic:not(.v-input--disabled) {
    // Use lighter border color matching Vue 2 style
    :deep(.v-field__outline) {
      color: rgba(0, 0, 0, 0.15);
    }

    // Adjust border radius to match design
    :deep(.v-field) {
      border-radius: 12px !important;
    }

    // Ensure proper field density to match Vue 2 sizing
    :deep(.v-field__input) {
      min-height: 48px;
      padding-top: 14px;
      padding-bottom: 14px;
    }
  }

  &:deep(textarea) {
    resize: none;
  }
}
</style>

<style lang="scss">
// Dark mode specific styles - unscoped to ensure proper cascade
.v-theme--dark {
  .v-textarea.brand-textarea.brand-textarea--basic:not(.v-input--disabled) {
    .v-field__outline {
      color: rgb(var(--v-theme-neutral50)) !important;
    }
  }
}
</style>
